import { T } from './lib/i18n'
import { lazy, Suspense, type ReactNode } from 'react'
import { LockKeyhole, LogOut } from 'lucide-react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AppShell } from './components/AppShell'
import { ErrorState, LoadingScreen } from './components/UI'
import { useAuth } from './context/AuthContext'
import { WorkspaceProvider, useWorkspace } from './context/WorkspaceContext'
import { AuthCallbackPage } from './pages/AuthCallbackPage'
import { OnboardingPage } from './pages/OnboardingPage'
import { SignInPage } from './pages/SignInPage'
import { JoinPage } from './pages/JoinPage'
import { TeamJoinPage } from './pages/TeamJoinPage'
import { LegalPage } from './pages/LegalPage'
import { SupportPage } from './pages/SupportPage'
import { HelpPage } from './pages/HelpPage'
import { LandingPage } from './pages/LandingPage'
import { subscriptionHasAccess } from './lib/subscriptions'

const DashboardPage = lazy(() => import('./pages/DashboardPage').then((module) => ({ default: module.DashboardPage })))
const SchedulePage = lazy(() => import('./pages/SchedulePage').then((module) => ({ default: module.SchedulePage })))
const ClientsPage = lazy(() => import('./pages/ClientsPage').then((module) => ({ default: module.ClientsPage })))
const MessagesPage = lazy(() => import('./pages/MessagesPage').then((module) => ({ default: module.MessagesPage })))
const TeamPage = lazy(() => import('./pages/TeamPage').then((module) => ({ default: module.TeamPage })))
const FinancePage = lazy(() => import('./pages/FinancePage').then((module) => ({ default: module.FinancePage })))
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((module) => ({ default: module.SettingsPage })))
const PlansPage = lazy(() => import('./pages/PlansPage').then((module) => ({ default: module.PlansPage })))
const AdminPage = lazy(() => import('./pages/AdminPage').then((module) => ({ default: module.AdminPage })))
const AdminSignInPage = lazy(() => import('./pages/AdminSignInPage').then((module) => ({ default: module.AdminSignInPage })))

function AuthenticatedWorkspace() {
  const { session, profile, profileError, loading, refreshProfile } = useAuth()
  const location = useLocation()
  if (loading) return <LoadingScreen />
  if (!session) return location.pathname === '/' ? <LandingPage locale="en" /> : <Navigate to="/signin" state={{ from: location.pathname }} replace />
  if (profileError) return <div className="standalone-error"><ErrorState message={profileError} retry={() => refreshProfile()} /></div>
  if (!profile?.onboarding_completed_at || !profile.default_account_kind) return <Navigate to="/onboarding" replace />
  return <WorkspaceProvider><WorkspaceGate /></WorkspaceProvider>
}

function WorkspaceGate() {
  const workspace = useWorkspace()
  if (workspace.loading) return <LoadingScreen />
  if (workspace.error) return <div className="standalone-error"><ErrorState message={workspace.error} retry={() => workspace.refresh()} /></div>
  if (workspace.accountKind === 'business' && workspace.subscription && !subscriptionHasAccess(workspace.subscription)) {
    return workspace.isOwner
      ? <Suspense fallback={<LoadingScreen />}><PlansPage accessGate /></Suspense>
      : <EmployeeAccessGate />
  }
  return <Suspense fallback={<LoadingScreen />}><AppShell /></Suspense>
}

function EmployeeAccessGate() {
  const { signOut } = useAuth()
  return (
    <main className="access-gate-page">
      <section className="access-gate-card">
        <span className="access-gate-card__icon"><LockKeyhole size={30} /></span>
        <span className="eyebrow"><T>{'Workspace paused'}</T></span>
        <h1><T>{'The company plan needs attention'}</T></h1>
        <p><T>{'Ask the business owner to choose or restore the company plan. Your role and team record are still safe.'}</T></p>
        <div className="access-gate-actions">
          <a className="button button--secondary" href="mailto:support@maintechsolution.com?subject=TidyPing%20plan%20support"><T>{'Contact support'}</T></a>
          <button className="button button--ghost" onClick={() => void signOut()}><LogOut size={17} /><T>{'Sign out'}</T></button>
        </div>
      </section>
    </main>
  )
}

function OnboardingGate() {
  const { session, profile, profileError, loading, refreshProfile } = useAuth()
  if (loading) return <LoadingScreen />
  if (!session) return <Navigate to="/signin" replace />
  if (profileError) return <div className="standalone-error"><ErrorState message={profileError} retry={() => refreshProfile()} /></div>
  if (profile?.onboarding_completed_at && profile.default_account_kind) return <Navigate to="/" replace />
  return <OnboardingPage />
}

function BusinessOnly({ children }: { children: ReactNode }) {
  const workspace = useWorkspace()
  return workspace.accountKind === 'business' ? children : <Navigate to="/" replace />
}

function OwnerOnly({ children }: { children: ReactNode }) {
  const workspace = useWorkspace()
  return workspace.accountKind === 'business' && workspace.membership?.role === 'owner'
    ? children
    : <Navigate to="/" replace />
}

function OwnerOrClientOnly({ children }: { children: ReactNode }) {
  const workspace = useWorkspace()
  return workspace.accountKind === 'client' || workspace.isOwner
    ? children
    : <Navigate to="/" replace />
}

function ClientOnly({ children }: { children: ReactNode }) {
  const workspace = useWorkspace()
  return workspace.accountKind === 'client' ? children : <Navigate to="/finance" replace />
}

export default function App() {
  return (
    <Routes>
      <Route path="/signin" element={<SignInPage />} />
      <Route path="/en" element={<LandingPage locale="en" />} />
      <Route path="/es" element={<LandingPage locale="es" />} />
      <Route path="/pt" element={<LandingPage locale="pt-BR" />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/join/:code" element={<JoinPage />} />
      <Route path="/team/join/:code" element={<TeamJoinPage />} />
      <Route path="/legal/:document" element={<LegalPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/admin/signin" element={<Suspense fallback={<LoadingScreen />}><AdminSignInPage /></Suspense>} />
      <Route path="/admin" element={<Suspense fallback={<LoadingScreen />}><AdminPage /></Suspense>} />
      <Route path="/onboarding" element={<OnboardingGate />} />
      <Route element={<AuthenticatedWorkspace />}>
        <Route index element={<DashboardPage />} />
        <Route path="schedule" element={<BusinessOnly><SchedulePage /></BusinessOnly>} />
        <Route path="clients" element={<OwnerOnly><ClientsPage /></OwnerOnly>} />
        <Route path="messages" element={<OwnerOrClientOnly><MessagesPage /></OwnerOrClientOnly>} />
        <Route path="team" element={<OwnerOnly><TeamPage /></OwnerOnly>} />
        <Route path="finance" element={<OwnerOnly><FinancePage /></OwnerOnly>} />
        <Route path="plans" element={<OwnerOnly><PlansPage /></OwnerOnly>} />
        <Route path="invoices" element={<ClientOnly><FinancePage /></ClientOnly>} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
